// src/utils/ticketGasGenerator.js
import { obtenerDatosEmpresa } from './tenantConfig';

// =====================================================
// HELPERS DE FECHA
// =====================================================
const formatearFechaTicket = (fechaISO) => {
  if (!fechaISO) return 'No disponible';
  try {
    const d = new Date(fechaISO);
    const dd = d.getDate().toString().padStart(2, '0');
    const mm = (d.getMonth() + 1).toString().padStart(2, '0');
    const yy = d.getFullYear();
    return `${dd}/${mm}/${yy}`;
  } catch { return fechaISO; }
};

const formatearHora12 = (fechaISO) => {
  if (!fechaISO) return '--:--';
  try {
    const d = new Date(fechaISO);
    let horas = d.getHours();
    const minutos = d.getMinutes().toString().padStart(2, '0');
    const ampm = horas >= 12 ? 'PM' : 'AM';
    horas = horas % 12 || 12;
    return `${horas}:${minutos} ${ampm}`;
  } catch { return '--:--'; }
};

const obtenerDiaSemana = (fechaISO) => {
  if (!fechaISO) return '';
  try {
    const d = new Date(fechaISO);
    const dias = ['DOMINGO', 'LUNES', 'MARTES', 'MIÉRCOLES', 'JUEVES', 'VIERNES', 'SÁBADO'];
    return dias[d.getDay()];
  } catch { return ''; }
};

const formatearFechaCompleta = (fechaISO) => {
  if (!fechaISO) return 'No disponible';
  try {
    const fecha = formatearFechaTicket(fechaISO);
    const hora12 = formatearHora12(fechaISO);
    const diaSemana = obtenerDiaSemana(fechaISO);
    return `${diaSemana} ${fecha} - ${hora12}`;
  } catch { return fechaISO; }
};

const formatearNumero = (valor, decimales = 2) => {
  if (valor === null || valor === undefined || valor === '') return '0.00';
  const num = Number(valor);
  if (isNaN(num)) return '0.00';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales
  }).format(num);
};

// ⭐ Etiquetas de estado para mostrar en el ticket
const labelEstadoTicket = (estado) => {
  const map = {
    'PENDIENTE': 'PENDIENTE',
    'CONFIRMADO': 'CONFIRMADO',
    'PAGO_PENDIENTE': 'PAGO EN VERIFICACION',
    'PAGO_VERIFICADO': 'PAGO VERIFICADO',
    'ASIGNADO': 'ASIGNADO A DELIVERY',
    'EN_CAMINO': 'EN CAMINO',
    'EN_PROCESO': 'ENTREGANDO',
    'COMPLETADO': 'COMPLETADO',
    'CANCELADO': 'CANCELADO',
  };
  return map[estado] || (estado || 'N/D');
};

// ⭐ Etiquetas de pago para mostrar en el ticket
const labelPagoTicket = (estadoPago) => {
  const map = {
    'pendiente': 'SIN PAGO',
    'por_verificar': 'POR VERIFICAR',
    'verificado': 'VERIFICADO',
    'parcial': 'PAGO PARCIAL',
    'pagado': 'PAGADO',
  };
  return map[estadoPago] || (estadoPago || 'SIN PAGO');
};

// =====================================================
// GENERAR HTML DEL TICKET
// =====================================================
export const generarHTMLTicketPedido = (pedido) => {
  const empresa = obtenerDatosEmpresa();
  const fechaPedido = pedido.fechaPedido || pedido.fechaCreacion || new Date();

  const detalles = pedido.detalles || [];
  const numeroPedido = pedido.numeroPedido || 'N/A';
  const nombreCliente = pedido.nombreCliente || 'CLIENTE';
  const cedulaCliente = pedido.cedulaCliente || '';
  const telefonoCliente = pedido.telefonoCliente || '';
  const direccion = pedido.direccionEntrega || 'N/D';
  const puntoReferencia = pedido.puntoReferencia || '';
  const urlMapa = pedido.urlMapaEntrega || '';
  const zona = pedido.nombreZona || 'Sin zona';
  const totalBs = Number(pedido.totalBs || 0);
  const estado = labelEstadoTicket(pedido.estado);
  const estadoPago = labelPagoTicket(pedido.estadoPago);

  // Detalles (productos) — SOLO EN Bs
  const detallesHtml = detalles.map(d => {
    const nombre = (d.nombreTipoRecarga || d.nombre || 'Recarga').slice(0, 26);
    const cant = d.cantidad || 1;
    const precio = Number(d.precioUnitarioBs || 0);
    const subtotal = Number(d.totalBs || (precio * cant));
    return `
      <div class="item-name">${nombre}</div>
      <div class="item-row">
        <span>${cant} x Bs ${formatearNumero(precio)}</span>
        <span class="item-total">Bs ${formatearNumero(subtotal)}</span>
      </div>
    `;
  }).join('');

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Ticket Pedido ${numeroPedido}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }

        body {
          font-family: 'Courier New', monospace;
          background: #fff;
          color: #000;
          width: 58mm;
          font-size: 10px;
          line-height: 1.35;
          font-weight: 700;
          text-transform: uppercase;
          text-align: center;
          margin: 0 auto;
          padding: 0;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }

        .ticket-wrapper {
          width: 52mm;
          padding: 2mm 1mm;
          margin: 0 auto;
          text-align: center;
          word-wrap: break-word;
        }

        .logo-img {
          width: 48mm;
          max-width: 48mm;
          margin: 0 auto 1mm auto;
          padding: 0;
          display: block;
        }

        .empresa-nombre {
          font-size: 11px;
          font-weight: 900;
          letter-spacing: 0.5px;
          margin-bottom: 1mm;
        }

        .empresa-dato {
          font-size: 9px;
          line-height: 1.3;
        }

        .separador {
          text-align: center;
          margin: 1.2mm 0;
          font-size: 9px;
          letter-spacing: 1px;
          color: #000;
        }

        .seccion-titulo {
          font-size: 10px;
          font-weight: 900;
          letter-spacing: 0.8px;
          margin: 1.5mm 0 1mm 0;
          padding: 0.8mm 0;
          border-top: 1px solid #000;
          border-bottom: 1px solid #000;
        }

        /* N° PEDIDO: pequeño, en 1 línea */
        .numero-pedido {
          font-size: 11px;
          font-weight: 900;
          letter-spacing: 0.5px;
          margin: 1.5mm 0;
          padding: 1.2mm 0;
          border-top: 1.5px solid #000;
          border-bottom: 1.5px solid #000;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .linea {
          text-align: center;
          margin: 0.6mm 0;
          font-size: 10px;
        }

        .linea-left {
          text-align: left;
          margin: 0.6mm 0;
          font-size: 10px;
        }

        .linea-bold {
          font-weight: 900;
        }

        .item-name {
          text-align: left;
          font-weight: 900;
          font-size: 10px;
          margin-top: 1mm;
        }

        .item-row {
          display: flex;
          justify-content: space-between;
          text-align: left;
          font-size: 9px;
          margin-bottom: 0.5mm;
        }

        .item-total {
          font-weight: 900;
        }

        .total-box {
          border: 2px solid #000;
          border-radius: 1.5mm;
          padding: 1.5mm;
          margin: 1.5mm 0;
        }

        .total-line {
          display: flex;
          justify-content: space-between;
          font-weight: 900;
          font-size: 12px;
          margin: 0.5mm 0;
        }

        .estado-box {
          border: 1.5px solid #000;
          border-radius: 1.5mm;
          padding: 1.2mm;
          margin: 1.5mm 0;
        }

        .estado-titulo {
          font-size: 8px;
          font-weight: 900;
          letter-spacing: 0.5px;
          margin-bottom: 0.5mm;
        }

        .estado-valor {
          font-size: 11px;
          font-weight: 900;
          letter-spacing: 0.3px;
        }

        .estado-pago {
          font-size: 9px;
          font-weight: 700;
          margin-top: 0.5mm;
        }

        .nota {
          font-size: 9px;
          line-height: 1.4;
          margin: 1mm 0;
          text-align: center;
        }

        .gracias {
          font-size: 11px;
          font-weight: 900;
          margin: 2mm 0 1mm 0;
          letter-spacing: 0.5px;
        }

        .redes {
          font-size: 11px;
          font-weight: 900;
          margin-top: 0.5mm;
          letter-spacing: 0.5px;
        }

        @media print {
          @page { size: 58mm auto; margin: 0; }
          .no-print { display: none; }
          body { width: 58mm; }
        }
      </style>
    </head>
    <body>
      <div class="ticket-wrapper">

        <!-- LOGO + EMPRESA -->
        <img src="${empresa.logoBase64}" alt="Logo" class="logo-img" />
        <div class="empresa-nombre">${empresa.nombreComercial || empresa.nombre || 'EXTRA GAS'}</div>
        ${empresa.rif ? `<div class="empresa-dato">RIF: ${empresa.rif}</div>` : ''}
        ${empresa.direccion ? `<div class="empresa-dato">${empresa.direccion}</div>` : ''}
        ${empresa.ciudad ? `<div class="empresa-dato">${empresa.ciudad}</div>` : ''}
        ${empresa.telefono1 || empresa.whatsapp
          ? `<div class="empresa-dato">${empresa.telefono1 || ''}${empresa.telefono1 && empresa.whatsapp ? ' / ' : ''}${empresa.whatsapp || ''}</div>`
          : ''}

        <div class="separador">================================</div>

        <!-- TÍTULO -->
        <div class="seccion-titulo">PEDIDO DE RECARGA</div>

        <!-- N° PEDIDO -->
        <div class="numero-pedido">N° ${numeroPedido}</div>

        <!-- FECHA -->
        <div class="seccion-titulo">FECHA DEL PEDIDO</div>
        <div class="linea">${formatearFechaCompleta(fechaPedido)}</div>

        <div class="separador">--------------------------------</div>

        <!-- CLIENTE -->
        <div class="seccion-titulo">DATOS DEL CLIENTE</div>
        <div class="linea linea-bold">${nombreCliente}</div>
        ${cedulaCliente ? `<div class="linea">CI: ${cedulaCliente}</div>` : ''}
        ${telefonoCliente ? `<div class="linea">TEL: ${telefonoCliente}</div>` : ''}

        <div class="separador">--------------------------------</div>

        <!-- DIRECCIÓN -->
        <div class="seccion-titulo">DIRECCIÓN DE ENTREGA</div>
        <div class="linea-left">${direccion}</div>
        ${puntoReferencia ? `<div class="linea-left">REF: ${puntoReferencia}</div>` : ''}
        <div class="linea-left linea-bold">ZONA: ${zona}</div>
        ${urlMapa ? `<div class="linea-left" style="font-size:8px;word-break:break-all;">MAPA: ${urlMapa.slice(0, 60)}${urlMapa.length > 60 ? '...' : ''}</div>` : ''}

        <div class="separador">--------------------------------</div>

        <!-- DETALLE -->
        <div class="seccion-titulo">DETALLE DEL PEDIDO</div>
        ${detallesHtml || '<div class="linea">Sin detalles</div>'}

        <div class="separador">================================</div>

        <!-- TOTAL (SOLO EN BS) -->
        <div class="total-box">
          <div class="total-line">
            <span>TOTAL:</span>
            <span>Bs ${formatearNumero(totalBs)}</span>
          </div>
        </div>

        <!-- ESTADO -->
        <div class="estado-box">
          <div class="estado-titulo">ESTADO DEL PEDIDO</div>
          <div class="estado-valor">${estado}</div>
          <div class="estado-pago">PAGO: ${estadoPago}</div>
        </div>

        <div class="separador">================================</div>

        <!-- INFO EXTRA -->
        <div class="nota">
          PARA CONSULTAR TU PEDIDO<br>
          INGRESA TU CÉDULA EN<br>
          NUESTRA PÁGINA WEB
        </div>

        <div class="separador">--------------------------------</div>

        <!-- GRACIAS -->
        <div class="gracias">¡GRACIAS POR TU PREFERENCIA!</div>
        <div class="nota">ENCUÉNTRANOS EN REDES COMO:</div>
        <div class="redes">${empresa.instagram || '@EXTRAGAS'}</div>

        ${empresa.slogan ? `
        <div class="separador">--------------------------------</div>
        <div class="nota">${empresa.slogan}</div>` : ''}

        ${empresa.email ? `<div class="nota">${empresa.email}</div>` : ''}

      </div>

      <div class="no-print" style="text-align: center; margin-top: 10px;">
        <button onclick="window.print()" style="padding: 8px 16px; background: #000; color: white; border: none; cursor: pointer; font-family: monospace; font-weight: 900; border-radius: 4px;">🖨️ IMPRIMIR</button>
        <button onclick="window.close()" style="padding: 8px 16px; background: #666; color: white; border: none; cursor: pointer; margin-left: 10px; font-family: monospace; font-weight: 900; border-radius: 4px;">✕ CERRAR</button>
      </div>

      <script>
        window.onload = function() { setTimeout(function() { window.print(); }, 300); };
        window.onafterprint = function() { setTimeout(function() { window.close(); }, 800); };
      </script>
    </body>
    </html>
  `;
};

// =====================================================
// ABRIR VENTANA E IMPRIMIR
// =====================================================
export const imprimirTicketPedido = (pedido) => {
  const html = generarHTMLTicketPedido(pedido);
  const ventana = window.open('', '_blank', 'width=400,height=650');
  if (!ventana) {
    alert('⚠️ Por favor permite ventanas emergentes para imprimir el ticket');
    return;
  }
  ventana.document.write(html);
  ventana.document.close();
};

export default {
  generarHTMLTicketPedido,
  imprimirTicketPedido
};